import {
  PendingWithholdingStatus,
  WithholdingTransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useQueryListInternalAccountTransactions } from "../../../query/banking/queries/useQueryListInternalAccountTransactions";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";

/**
 * Encapsulates the tax-withholding payment scheduling state for the active
 * tax-withholding account: scheduled (pending) and processing payments, their
 * amounts, and whether a tax payment is already scheduled/processing.
 *
 * Shared by the Tax Withholdings dashboard toolbar (to hide the "Schedule tax
 * payment" action when a payment already exists — backend rejects duplicates
 * with "A pending tax payment already exists") and the AvailableBalance card.
 */
export function useTaxPaymentScheduling() {
  const queryInternalAccounts = useQueryInternalAccounts();
  const internalAccount = queryInternalAccounts.data?.find(
    selectorActiveTaxWithholdingAccount
  );

  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!internalAccount
    }
  );

  const queryProcessingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Processing"
    },
    {
      enabled: !!internalAccount
    }
  );

  const internalPendingTransactions = useMemo(() => {
    return (queryPendingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryPendingTransactions.data?.pages]);

  const internalProcessingTransactions = useMemo(() => {
    return (queryProcessingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryProcessingTransactions.data?.pages]);

  const scheduledPayments = useMemo(() => {
    return internalPendingTransactions.filter(
      transaction =>
        transaction.status === PendingWithholdingStatus.Pending &&
        (transaction.type as WithholdingTransactionType) ===
          WithholdingTransactionType.TaxPayment
    );
  }, [internalPendingTransactions]);

  const processingPayments = useMemo(() => {
    const processing = internalProcessingTransactions.filter(
      transaction => transaction.status === "Processing"
    );
    const pendingNonTaxPayments = internalPendingTransactions.filter(
      transaction =>
        transaction.status === PendingWithholdingStatus.Pending &&
        (transaction.type as WithholdingTransactionType) !==
          WithholdingTransactionType.TaxPayment
    );
    return [...processing, ...pendingNonTaxPayments];
  }, [internalProcessingTransactions, internalPendingTransactions]);

  const scheduledPaymentAmount = useMemo(
    () => scheduledPayments.reduce((sum, t) => sum + t.amount, 0),
    [scheduledPayments]
  );

  const processingPaymentAmount = useMemo(
    () => processingPayments.reduce((sum, t) => sum + t.amount, 0),
    [processingPayments]
  );

  const hasScheduledPayment = scheduledPayments.length > 0;
  const hasProcessingPayment = processingPayments.length > 0;
  const hasScheduledTaxPayment = scheduledPayments.some(
    t =>
      (t.type as WithholdingTransactionType) ===
      WithholdingTransactionType.TaxPayment
  );
  const hasProcessingTaxPayment = processingPayments.some(
    t =>
      (t.type as WithholdingTransactionType) ===
      WithholdingTransactionType.TaxPayment
  );

  // True until both the pending and processing transaction queries have
  // resolved for the first time. Consumers use this to avoid showing a
  // "Schedule tax payment" action before we know whether one already exists
  // (which would let the user submit a duplicate the backend rejects).
  const isLoading =
    queryPendingTransactions.isInitialLoading ||
    queryProcessingTransactions.isInitialLoading;

  return {
    scheduledPayments,
    processingPayments,
    scheduledPaymentAmount,
    processingPaymentAmount,
    hasScheduledPayment,
    hasProcessingPayment,
    hasScheduledTaxPayment,
    hasProcessingTaxPayment,
    isLoading
  };
}
